import moment from 'moment'
import 'moment-duration-format'
import {
    ACTIVITY_FILTERS,
    ARC_FILTERS,
    DATE_FILTERS,
    DURATION_FILTERS,
    ATTRIBUTES_FILTERS,
    VARIANT_FILTERS,
    LOOP_FILTERS,
    NVARIANTS_FILTERS,
    TRACES_FILTERS,
    FILTERS_TYPES,
    ATTRIBUTTE_TYPES,
} from '@/screens/Project/Process/Filters/FilterModal/filtersEntity'
import { isFilterSet } from './isFilterSet'

export function getFilterIconId(filter = {}) {
    if (isFilterSet(filter)) return 'FILTER_SET'

    // Attribute filters may come without `kind` from the backend.
    if (ATTRIBUTES_FILTERS.includes(filter.kind)) return filter.kind
    if (/^TRACE_ATTRIBUTES_NUM_/.test(filter.id || '')) return 'NUMERIC_ATTRIBUTE'
    if (/^TRACE_ATTRIBUTES_CTG_/.test(filter.id || '')) return 'CATEGORICAL_ATTRIBUTE'
    if (/^TRACE_ATTRIBUTES_BOOL_/.test(filter.id || '')) return 'BOOLEAN_ATTRIBUTE'
    if (/^TRACE_ATTRIBUTES_DATE_/.test(filter.id || '')) return 'DATETIME_ATTRIBUTE'

    return filter.id
}

function ensureArray(value) {
    if (value === undefined || value === null) return []
    return Array.isArray(value) ? value : [value]
}

function joinValues(values) {
    return ensureArray(values).filter((v) => v !== undefined && v !== null).join(', ')
}

function getLabel(filter, t) {
    if (isFilterSet(filter)) return filter.name || '-'

    const translateIfExists = (key, fallback) => {
        const translated = t(key)
        return translated === key ? fallback : translated
    }

    // Homogeneous rule: prefer i18n by id/kind; backend-provided `label` is only a fallback.
    const idFallback = filter.label || filter.id || '-'
    const translatedById = translateIfExists(`visualization.filters.labels.${filter.id}`, null)
    if (translatedById !== null) return translatedById

    // Some callers store `label` as an i18n key; try it only if id had no translation.
    if (filter.label) {
        return translateIfExists(`visualization.filters.labels.${filter.label}`, filter.label)
    }

    return idFallback
}

function getAttributeConstraintLabel(constraintId) {
    const all = []
    Object.values(ATTRIBUTTE_TYPES).forEach((getTypes) => {
        const types = getTypes()
        types.forEach((type) => all.push(type))
    })
    return (all.find((a) => a.value === constraintId) || {}).label
}

export function formatFilterForDisplay(filter = {}, { t, variants, durationFormat } = {}) {
    const meta = filter.meta || {}

    if (isFilterSet(filter)) {
        const count = (filter.filters || []).length
        const numberFilters = count > 1 ? 'filters' : 'filter'
        const suffix = t ? t(`visualization.filters.filterSets.config.filterSetsTable.${numberFilters}`) : numberFilters
        return {
            label: filter.name || '-',
            value: `${count} ${suffix}`,
            tooltip: `${filter.name || '-'}: ${count} ${suffix}`,
        }
    }

    const { id, kind } = filter
    const values = ensureArray(filter.values ?? filter.value ?? filter.key)

    const label = t ? getLabel(filter, t) : (filter.label || filter.id)

    const isActivity = ACTIVITY_FILTERS.includes(id) || ACTIVITY_FILTERS.includes(kind)
    const isArc = ARC_FILTERS.includes(id) || ARC_FILTERS.includes(kind)
    const isDuration = DURATION_FILTERS.includes(id) || DURATION_FILTERS.includes(kind)
    const isDate = DATE_FILTERS.includes(id) || DATE_FILTERS.includes(kind)
    const isAttribute = ATTRIBUTES_FILTERS.includes(kind) || ATTRIBUTES_FILTERS.includes(id) || /^TRACE_ATTRIBUTES_(CTG|BOOL|NUM|DATE)_/.test(id || '')
    const isVariant = VARIANT_FILTERS.includes(id) || VARIANT_FILTERS.includes(kind)
    const isLoop = LOOP_FILTERS.includes(id) || LOOP_FILTERS.includes(kind)
    const isNVariants = NVARIANTS_FILTERS.includes(id) || NVARIANTS_FILTERS.includes(kind)
    const isTraces = TRACES_FILTERS.includes(id)

    if (isActivity || isArc) {
        const value = joinValues(values)
        return {
            label,
            value,
            tooltip: `${label}: ${value}`,
        }
    }

    if (isDuration) {
        const rawDuration = id.includes('ACTIVITIES') ? values[values.length - 1] : values[0]
        const DEFAULT_DURATION_FORMAT = 'y[Y] M[M] d[D] h[h] m[m] s[s]'
        const format = durationFormat || DEFAULT_DURATION_FORMAT
        const formatOptions = durationFormat ? undefined : { trim: 'all' }

        let value = rawDuration
            ? moment.duration(rawDuration).format(format, formatOptions)
            : null

        if (id.includes('ACTIVITIES')) {
            if (id.endsWith('LESS_THAN')) value = `< ${value}`
            if (id.endsWith('GREATER_THAN')) value = `> ${value}`
        }

        const unwrap = (maybe) => (maybe && typeof maybe === 'object' ? maybe.value : maybe)

        let additionalValue = unwrap(meta.arc)
            || unwrap(filter.arc)
            || unwrap((filter.type || {}).arc)
            || unwrap(meta.activity)
            || unwrap(filter.activity)
            || unwrap((filter.type || {}).category)
        if (!additionalValue && id.includes('ACTIVITIES')) {
            if (values.length === 5) {
                additionalValue = `${values[0]} (${values[1]}) - ${values[2]} (${values[3]})`
            } else if (values.length === 3) {
                additionalValue = `${values[0]}-${values[1]}`
            } else if (meta.activitySource && meta.activityTarget) {
                additionalValue = `${meta.activitySource}-${meta.activityTarget}`
            }
        }

        return {
            label,
            value,
            additionalValue,
            tooltip: `${label}: ${value}${additionalValue ? ` | ${additionalValue}` : ''}`,
        }
    }

    if (isDate) {
        const [value] = values
        const rawActivity = meta.activity || filter.activity || (filter.type || {}).category
        const activity = rawActivity && typeof rawActivity === 'object' ? rawActivity.value : rawActivity
        return {
            label,
            value: value ?? '',
            tooltip: `${label}: ${value ?? ''}${activity ? ` | ${activity}` : ''}`,
        }
    }

    if (isAttribute) {
        const isBooleanType = `${id || ''}`.includes(FILTERS_TYPES.ATTRIB_BOOLEAN) || `${kind || ''}`.includes(FILTERS_TYPES.ATTRIB_BOOLEAN)
        if (isBooleanType) {
            const rawAttribute = meta.attribute || filter.attribute
            const attribute = rawAttribute && typeof rawAttribute === 'object' ? rawAttribute.value : rawAttribute
            const constraintLabel = getAttributeConstraintLabel(id)
            const boolLabel = attribute && constraintLabel ? `${attribute} - ${constraintLabel}` : (filter.label || label)
            return {
                label: boolLabel,
                value: '',
                tooltip: boolLabel,
            }
        }

        const rawAttribute = meta.attribute || filter.attribute
        const attribute = rawAttribute && typeof rawAttribute === 'object' ? rawAttribute.value : rawAttribute
        const constraintLabel = getAttributeConstraintLabel(id)
        const computedLabel = attribute && constraintLabel ? `${attribute} - ${constraintLabel}` : (filter.label || label)

        const value = Array.isArray(values[0]) ? joinValues(values[0]) : joinValues(values)
        return {
            label: computedLabel,
            value,
            tooltip: `${computedLabel}: ${value}`,
        }
    }

    if (isVariant) {
        const namesFromMeta = ensureArray(meta.variantName || (filter.type || {}).variantName)
        const nameList = namesFromMeta.length
            ? namesFromMeta
            : (ensureArray(values)
                .map((variantId) => (variants || []).find((v) => v.id === variantId))
                .filter(Boolean)
                .map((v) => v.name))

        const value = nameList.length
            ? nameList.map((name) => `${name}`.replace('Variant ', '')).join(', ')
            : '-'

        return {
            label,
            value,
            tooltip: `${label}: ${value}`,
        }
    }

    if (isNVariants) {
        const value = values?.[0] ?? '-'
        return {
            label,
            value,
            tooltip: `${label}: ${value}`,
        }
    }

    if (isLoop) {
        const value = meta.loopName || (filter.type || {}).loopName || '-'
        return {
            label,
            value,
            tooltip: `${label}: ${value}`,
        }
    }

    if (isTraces) {
        const value = joinValues(values)
        return {
            label,
            value,
            tooltip: `${label}: ${value}`,
        }
    }

    const fallbackValue = joinValues(values)
    return {
        label,
        value: fallbackValue,
        tooltip: `${label}: ${fallbackValue}`,
    }
}
